import React, { useCallback, useEffect, useState } from 'react';
import ActivityFeedCmp from 'src/components/ActivityFeedCmp';
import ActivityFeedSkeleton from 'src/components/ActivityFeedCmp/ActivityFeedSkeleton';
import CardCmp from 'src/components/CardCmp';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { activitiesData, activitiesPaginationData, appendActivities, setActivities, setActivitiesLoading, setActivitiesPagination } from 'src/redux/Quoting.slice';
import { getQuoteActivities } from 'src/services/QuoteService';
import WalToast from 'src/utils/WalToast';

import { useRadarData } from '../../hooks/useRadarData';

const LatestActivitySection: React.FC = () => {
    const dispatch = useAppDispatch();
    const { trackOrderDetail, isDetailLoading } = useRadarData();
    const pagination = useAppSelector(activitiesPaginationData);
    const ordActivities = useAppSelector(activitiesData);
    const [isAccordionOpen, setIsAccordionOpen] = useState(true);

    const masterOrderId = trackOrderDetail?.masterOrderId ?? null;

    const fetchActivities = useCallback(
        async ({ lastId, append }: { lastId?: number | null; append: boolean }) => {
            if (!masterOrderId) return;

            dispatch(setActivitiesLoading(true));

            getQuoteActivities({ entityType: 'order', entityId: masterOrderId, perPage: 25, ...(lastId ? { lastId } : {}) })
                .then((response: any) => {
                    const data = response?.data;

                    if (append) {
                        dispatch(appendActivities(data?.activities || []));
                    } else {
                        dispatch(setActivities(data?.activities || []));
                    }

                    dispatch(
                        setActivitiesPagination(
                            data?.pagination
                                ? {
                                      hasMore: data.pagination.hasMore ?? false,
                                      nextCursor: data.pagination.nextCursor ?? null,
                                      totalItems: data.pagination.totalItems ?? 0,
                                  }
                                : null
                        )
                    );
                })
                .catch((err: any) => {
                    WalToast.error(err?.response?.data?.message || 'Failed to load activities');
                })
                .finally(() => {
                    dispatch(setActivitiesLoading(false));
                });
        },
        [masterOrderId, dispatch]
    );

    useEffect(() => {
        if (!masterOrderId) return;
        fetchActivities({ append: false });

        return () => {
            dispatch(setActivities([]));
            dispatch(setActivitiesPagination(null));
        };
    }, [masterOrderId]);

    const handleLoadMore = useCallback(() => {
        if (!pagination?.hasMore || !pagination?.nextCursor) return;
        fetchActivities({ lastId: pagination.nextCursor, append: true });
    }, [pagination, fetchActivities]);

    return (
        <CardCmp
            headerTitle="Latest Activity"
            childrenClassName="widget-card-content-wrap !pb-0 !px-0 !pt-0"
            isAccordion={ordActivities && ordActivities?.length > 5 ? true : false}
            hideContentOnCollapse={false}
            scrollToOnOpen={true}
            onAccordionToggle={(isOpen) => setIsAccordionOpen(isOpen)}
            headerParentClassName="!px-4"
            isCardContentWithoutSpacing={true}
            isHeaderWithBg
            isFooter={false}
        >
            {masterOrderId ? (
                <div className="flex flex-col min-h-0">
                    <ActivityFeedCmp
                        entityType="order"
                        entityId={masterOrderId}
                        handleLoadMore={handleLoadMore}
                        isRadarWidget={true}
                        // Chat-style like WAL radar: newest at the bottom, scroll up to load older rows.
                        isNewestAtBottom={true}
                        notePlaceholder="Add an activity or note"
                        isAccordionOpen={isAccordionOpen}
                        inputPosition="bottom"
                    />
                </div>
            ) : isDetailLoading ? (
                <div className="flex flex-col px-4 pt-5">
                    <ActivityFeedSkeleton />
                </div>
            ) : null}
        </CardCmp>
    );
};

export default LatestActivitySection;
